/**
 * Owner-facing Supabase Production CLI status (#520). Non-secret only: the
 * browser never receives, asks for or holds the database password.
 */

import { isSupabaseCliCredentialHealth, type SupabaseCliCredentialHealth } from '@brunel/shared';

export interface SupabaseCliStatusCredential {
  readonly projectRef: string;
  readonly credentialRef: string;
  readonly designatedNodeId: string;
  readonly designatedNodeName: string | null;
  readonly health: SupabaseCliCredentialHealth;
  readonly pendingOwnerAction: 'enroll' | 'reauth' | 'unlock' | null;
  readonly lastVerifiedAt: string | null;
  readonly enrolledAt: string | null;
  readonly revokedAt: string | null;
  readonly lastRunOperation: string | null;
  readonly lastRunStatus: string | null;
  readonly lastRunAt: string | null;
}

export interface SupabaseCliStatus {
  readonly connectorAvailable: boolean;
  readonly credential: SupabaseCliStatusCredential | null;
}

const str = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);

export function parseSupabaseCliStatus(value: unknown): SupabaseCliStatus | null {
  if (value === null || typeof value !== 'object') return null;
  const v = value as Record<string, unknown>;
  if (typeof v.connectorAvailable !== 'boolean') return null;
  if (v.credential === null) return { connectorAvailable: v.connectorAvailable, credential: null };
  if (v.credential === undefined || typeof v.credential !== 'object') return null;
  const c = v.credential as Record<string, unknown>;
  const projectRef = str(c.projectRef);
  const designatedNodeId = str(c.designatedNodeId);
  if (projectRef === null || designatedNodeId === null || !isSupabaseCliCredentialHealth(c.health)) return null;
  const action = c.pendingOwnerAction;
  return {
    connectorAvailable: v.connectorAvailable,
    credential: {
      projectRef,
      credentialRef: str(c.credentialRef) ?? '',
      designatedNodeId,
      designatedNodeName: str(c.designatedNodeName),
      health: c.health,
      pendingOwnerAction: action === 'enroll' || action === 'reauth' || action === 'unlock' ? action : null,
      lastVerifiedAt: str(c.lastVerifiedAt),
      enrolledAt: str(c.enrolledAt),
      revokedAt: str(c.revokedAt),
      lastRunOperation: str(c.lastRunOperation),
      lastRunStatus: str(c.lastRunStatus),
      lastRunAt: str(c.lastRunAt),
    },
  };
}

export function enrollCommand(projectRef: string): string {
  return `brunel-node supabase-cli enroll --project-ref ${projectRef}`;
}

export interface SupabaseCliView {
  readonly connectorLabel: string;
  readonly credentialLabel: string;
  readonly projectLabel: string;
  readonly lastVerifiedLabel: string | null;
  /** The one owner action, when there is one. Never a password field. */
  readonly ownerAction: string | null;
  readonly canTest: boolean;
  readonly canRevoke: boolean;
  readonly canDesignate: boolean;
}

export function buildSupabaseCliView(status: SupabaseCliStatus | null): SupabaseCliView {
  const connectorLabel = status === null ? 'Unknown' : status.connectorAvailable ? 'Connected' : 'Unavailable';
  const c = status?.credential ?? null;
  if (c === null || c.revokedAt !== null) {
    return {
      connectorLabel,
      credentialLabel: c === null ? 'Not designated' : 'Revoked',
      projectLabel: c?.projectRef ?? 'Not set',
      lastVerifiedLabel: null,
      ownerAction: 'Designate the deployment computer, then enroll the production DB password once on it.',
      canTest: false,
      canRevoke: false,
      canDesignate: true,
    };
  }
  const node = c.designatedNodeName ?? 'the designated computer';
  const credentialLabel = c.health === 'available'
    ? `Ready on ${node}`
    : c.health === 'unknown'
      ? `Enrolled on ${node} · not yet verified`
      : c.health === 'missing'
        ? 'Enrollment required'
        : c.health === 'reauth_required'
          ? 'Reauth required'
          : `Credential store locked on ${node}`;
  const ownerAction = c.pendingOwnerAction === 'enroll'
    ? `On ${node}, in a local Terminal: ${enrollCommand(c.projectRef)}`
    : c.pendingOwnerAction === 'reauth'
      ? `The database rejected the password. On ${node}, re-enroll: ${enrollCommand(c.projectRef)}`
      : c.pendingOwnerAction === 'unlock'
        ? `Unlock the login Keychain on ${node}, then Test credential.`
        : null;
  return {
    connectorLabel,
    credentialLabel,
    projectLabel: c.projectRef,
    lastVerifiedLabel: c.lastVerifiedAt,
    ownerAction,
    canTest: true,
    canRevoke: true,
    canDesignate: false,
  };
}
